// ─── Flag emoji from ISO 2-letter code ───
export const getFlagEmoji = (countryCode) => {
  if (!countryCode || countryCode.length < 2) return '';
  const code = countryCode.toUpperCase().slice(0, 2);
  return code.split('').map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('');
};

// ─── Lookup helpers (handles both codes "FR" and names "France") ───
const _nameToCode = {};
const _codeToName = {};
const _initLookups = (countries) => {
  countries.forEach(c => {
    _nameToCode[c.name.toLowerCase()] = c.code;
    _codeToName[c.code.toUpperCase()] = c.name;
  });
};

export const resolveCountryCode = (input) => {
  if (!input) return '';
  const upper = input.toUpperCase();
  if (_codeToName[upper]) return upper;
  return _nameToCode[input.toLowerCase()] || input.toUpperCase().slice(0, 2);
};

export const resolveCountryName = (input) => {
  if (!input) return '';
  const upper = input.toUpperCase();
  if (_codeToName[upper]) return _codeToName[upper];
  // Already a name?
  if (_nameToCode[input.toLowerCase()]) return input;
  return input;
};

export const getCountryWithFlag = (input) => {
  const code = resolveCountryCode(input);
  const name = resolveCountryName(input);
  return { code, name, flag: getFlagEmoji(code) };
};

export const DEFAULT_COUNTRIES = [
    { name: 'Cameroun', code: 'CM', currency: 'XAF', delivery_time_days: 7 },
    { name: 'France', code: 'FR', currency: 'EUR', delivery_time_days: 3 },
    { name: 'Chine', code: 'CN', currency: 'CNY', delivery_time_days: 14 },
    { name: "Côte d'Ivoire", code: 'CI', currency: 'XOF', delivery_time_days: 7 },
    { name: 'Sénégal', code: 'SN', currency: 'XOF', delivery_time_days: 7 },
    { name: 'Gabon', code: 'GA', currency: 'XAF', delivery_time_days: 7 },
    { name: 'Congo', code: 'CG', currency: 'XAF', delivery_time_days: 8 },
    { name: 'Belgique', code: 'BE', currency: 'EUR', delivery_time_days: 3 },
    { name: 'Allemagne', code: 'DE', currency: 'EUR', delivery_time_days: 3 },
    { name: 'États-Unis', code: 'US', currency: 'USD', delivery_time_days: 10 },
    { name: 'Canada', code: 'CA', currency: 'CAD', delivery_time_days: 10 },
    { name: 'Royaume-Uni', code: 'GB', currency: 'GBP', delivery_time_days: 4 },
];

// Initialize lookups from DEFAULT_COUNTRIES
_initLookups(DEFAULT_COUNTRIES);

// ─── Phone prefixes by country ───
export const PHONE_PREFIXES = {
  FR: { dialCode: '+33', digits: 9, placeholder: '6 12 34 56 78' },
  CM: { dialCode: '+237', digits: 9, placeholder: '6 XX XX XX XX' },
  CN: { dialCode: '+86', digits: 11, placeholder: '138 0000 0000' },
  CI: { dialCode: '+225', digits: 10, placeholder: '07 00 00 00 00' },
  SN: { dialCode: '+221', digits: 9, placeholder: '77 000 00 00' },
  GA: { dialCode: '+241', digits: 7, placeholder: '74 00 00 0' },
  CG: { dialCode: '+242', digits: 9, placeholder: '06 000 00 00' },
  BE: { dialCode: '+32', digits: 9, placeholder: '470 00 00 00' },
  DE: { dialCode: '+49', digits: 10, placeholder: '170 0000 000' },
  US: { dialCode: '+1', digits: 10, placeholder: '202 555 0100' },
  CA: { dialCode: '+1', digits: 10, placeholder: '514 555 0100' },
  GB: { dialCode: '+44', digits: 10, placeholder: '7911 123456' },
};

// Countries where leading 0 is convention for local numbers
export const STRIP_LEADING_ZERO_COUNTRIES = ['FR', 'BE', 'DE', 'CI', 'CG', 'GB'];

export const DEFAULT_CITIES_BY_COUNTRY = {
    CM: [
        { name: 'Douala', code: 'DLA', delivery_time_days: 5 },
        { name: 'Yaoundé', code: 'YAO', delivery_time_days: 6 },
        { name: 'Bafoussam', code: 'BFX', delivery_time_days: 7 },
        { name: 'Bamenda', code: 'BPC', delivery_time_days: 8 },
        { name: 'Kribi', code: 'KBI', delivery_time_days: 7 },
        { name: 'Limbé', code: 'LMB', delivery_time_days: 7 },
        { name: 'Garoua', code: 'GOU', delivery_time_days: 9 },
        { name: 'Maroua', code: 'MVR', delivery_time_days: 10 },
        { name: 'Bertoua', code: 'BTA', delivery_time_days: 8 },
        { name: 'Ebolowa', code: 'EBW', delivery_time_days: 8 },
    ],
    FR: [
        { name: 'Paris', code: 'PAR', delivery_time_days: 2 },
        { name: 'Lyon', code: 'LYS', delivery_time_days: 3 },
        { name: 'Marseille', code: 'MRS', delivery_time_days: 3 },
        { name: 'Toulouse', code: 'TLS', delivery_time_days: 3 },
        { name: 'Bordeaux', code: 'BOD', delivery_time_days: 3 },
        { name: 'Lille', code: 'LIL', delivery_time_days: 3 },
        { name: 'Strasbourg', code: 'SXB', delivery_time_days: 3 },
        { name: 'Nice', code: 'NCE', delivery_time_days: 3 },
        { name: 'Nantes', code: 'NTE', delivery_time_days: 3 },
        { name: 'Montpellier', code: 'MPL', delivery_time_days: 3 },
    ],
    CN: [
        { name: 'Guangzhou', code: 'CAN', delivery_time_days: 12 },
        { name: 'Shanghai', code: 'SHA', delivery_time_days: 12 },
        { name: 'Beijing', code: 'PEK', delivery_time_days: 13 },
        { name: 'Shenzhen', code: 'SZX', delivery_time_days: 12 },
        { name: 'Yiwu', code: 'YIW', delivery_time_days: 13 },
        { name: 'Hangzhou', code: 'HGH', delivery_time_days: 13 },
        { name: 'Ningbo', code: 'NGB', delivery_time_days: 13 },
        { name: 'Foshan', code: 'FOS', delivery_time_days: 12 },
        { name: 'Dongguan', code: 'DGG', delivery_time_days: 12 },
        { name: 'Chengdu', code: 'CTU', delivery_time_days: 14 },
    ],
    CI: [
        { name: 'Abidjan', code: 'ABJ', delivery_time_days: 6 },
        { name: 'Yamoussoukro', code: 'ASK', delivery_time_days: 7 },
        { name: 'Bouaké', code: 'BYK', delivery_time_days: 7 },
        { name: 'San-Pédro', code: 'SPY', delivery_time_days: 8 },
        { name: 'Korhogo', code: 'HGO', delivery_time_days: 8 },
        { name: 'Daloa', code: 'DJO', delivery_time_days: 8 },
        { name: 'Man', code: 'MJC', delivery_time_days: 8 },
        { name: 'Gagnoa', code: 'GGN', delivery_time_days: 8 },
        { name: 'Divo', code: 'DIV', delivery_time_days: 8 },
        { name: 'Abengourou', code: 'OGO', delivery_time_days: 8 },
    ],
    SN: [
        { name: 'Dakar', code: 'DSS', delivery_time_days: 6 },
        { name: 'Thiès', code: 'THS', delivery_time_days: 7 },
        { name: 'Touba', code: 'TBA', delivery_time_days: 7 },
        { name: 'Saint-Louis', code: 'SLU', delivery_time_days: 7 },
        { name: 'Rufisque', code: 'RUF', delivery_time_days: 7 },
        { name: 'Kaolack', code: 'KLK', delivery_time_days: 7 },
        { name: 'Ziguinchor', code: 'ZIG', delivery_time_days: 8 },
        { name: 'Mbour', code: 'MBR', delivery_time_days: 7 },
        { name: 'Diourbel', code: 'DBL', delivery_time_days: 7 },
        { name: 'Louga', code: 'LGA', delivery_time_days: 7 },
    ],
    GA: [
        { name: 'Libreville', code: 'LBV', delivery_time_days: 6 },
        { name: 'Port-Gentil', code: 'POG', delivery_time_days: 7 },
        { name: 'Franceville', code: 'MVB', delivery_time_days: 8 },
        { name: 'Oyem', code: 'OYE', delivery_time_days: 8 },
        { name: 'Moanda', code: 'MFF', delivery_time_days: 8 },
        { name: 'Lambaréné', code: 'LBQ', delivery_time_days: 7 },
        { name: 'Mouila', code: 'MJL', delivery_time_days: 8 },
        { name: 'Tchibanga', code: 'TCH', delivery_time_days: 8 },
        { name: 'Koulamoutou', code: 'KOU', delivery_time_days: 8 },
        { name: 'Makokou', code: 'MKU', delivery_time_days: 9 },
    ],
    CG: [
        { name: 'Brazzaville', code: 'BZV', delivery_time_days: 7 },
        { name: 'Pointe-Noire', code: 'PNR', delivery_time_days: 7 },
        { name: 'Dolisie', code: 'DIS', delivery_time_days: 8 },
        { name: 'Nkayi', code: 'NKY', delivery_time_days: 8 },
        { name: 'Ouesso', code: 'OUE', delivery_time_days: 9 },
        { name: 'Owando', code: 'FTX', delivery_time_days: 9 },
        { name: 'Madingou', code: 'MGO', delivery_time_days: 8 },
        { name: 'Gamboma', code: 'GBM', delivery_time_days: 8 },
        { name: 'Impfondo', code: 'ION', delivery_time_days: 9 },
        { name: 'Sibiti', code: 'SBT', delivery_time_days: 8 },
    ],
    BE: [
        { name: 'Bruxelles', code: 'BRU', delivery_time_days: 2 },
        { name: 'Anvers', code: 'ANR', delivery_time_days: 3 },
        { name: 'Gand', code: 'GNE', delivery_time_days: 3 },
        { name: 'Charleroi', code: 'CRL', delivery_time_days: 3 },
        { name: 'Liège', code: 'LGG', delivery_time_days: 3 },
        { name: 'Bruges', code: 'BGS', delivery_time_days: 3 },
        { name: 'Namur', code: 'NAM', delivery_time_days: 3 },
        { name: 'Louvain', code: 'LVN', delivery_time_days: 3 },
        { name: 'Mons', code: 'MNS', delivery_time_days: 3 },
        { name: 'Alost', code: 'ALS', delivery_time_days: 3 },
    ],
    DE: [
        { name: 'Berlin', code: 'BER', delivery_time_days: 3 },
        { name: 'Francfort', code: 'FRA', delivery_time_days: 3 },
        { name: 'Munich', code: 'MUC', delivery_time_days: 3 },
        { name: 'Hambourg', code: 'HAM', delivery_time_days: 3 },
        { name: 'Cologne', code: 'CGN', delivery_time_days: 3 },
        { name: 'Stuttgart', code: 'STR', delivery_time_days: 3 },
        { name: 'Düsseldorf', code: 'DUS', delivery_time_days: 3 },
        { name: 'Dortmund', code: 'DTM', delivery_time_days: 3 },
        { name: 'Essen', code: 'ESS', delivery_time_days: 3 },
        { name: 'Leipzig', code: 'LEJ', delivery_time_days: 3 },
    ],
    US: [
        { name: 'New York', code: 'JFK', delivery_time_days: 8 },
        { name: 'Los Angeles', code: 'LAX', delivery_time_days: 10 },
        { name: 'Chicago', code: 'ORD', delivery_time_days: 9 },
        { name: 'Houston', code: 'IAH', delivery_time_days: 9 },
        { name: 'Atlanta', code: 'ATL', delivery_time_days: 9 },
        { name: 'Miami', code: 'MIA', delivery_time_days: 9 },
        { name: 'Dallas', code: 'DFW', delivery_time_days: 9 },
        { name: 'Washington', code: 'IAD', delivery_time_days: 9 },
        { name: 'Boston', code: 'BOS', delivery_time_days: 9 },
        { name: 'San Francisco', code: 'SFO', delivery_time_days: 10 },
    ],
    CA: [
        { name: 'Montréal', code: 'YUL', delivery_time_days: 9 },
        { name: 'Toronto', code: 'YYZ', delivery_time_days: 9 },
        { name: 'Vancouver', code: 'YVR', delivery_time_days: 10 },
        { name: 'Ottawa', code: 'YOW', delivery_time_days: 9 },
        { name: 'Calgary', code: 'YYC', delivery_time_days: 10 },
        { name: 'Edmonton', code: 'YEG', delivery_time_days: 10 },
        { name: 'Québec', code: 'YQB', delivery_time_days: 9 },
        { name: 'Winnipeg', code: 'YWG', delivery_time_days: 10 },
        { name: 'Hamilton', code: 'YHM', delivery_time_days: 9 },
        { name: 'Gatineau', code: 'YND', delivery_time_days: 9 },
    ],
    GB: [
        { name: 'Londres', code: 'LHR', delivery_time_days: 3 },
        { name: 'Manchester', code: 'MAN', delivery_time_days: 4 },
        { name: 'Birmingham', code: 'BHX', delivery_time_days: 4 },
        { name: 'Liverpool', code: 'LPL', delivery_time_days: 4 },
        { name: 'Leeds', code: 'LBA', delivery_time_days: 4 },
        { name: 'Glasgow', code: 'GLA', delivery_time_days: 4 },
        { name: 'Bristol', code: 'BRS', delivery_time_days: 4 },
        { name: 'Édimbourg', code: 'EDI', delivery_time_days: 4 },
        { name: 'Sheffield', code: 'SHE', delivery_time_days: 4 },
        { name: 'Cardiff', code: 'CWL', delivery_time_days: 4 },
    ],
};
